import { useEffect, useState } from 'react';
import { useSendInvoiceToCustomer, useCustomerSearch } from './hooks/useInvoices';
import Button from '../../shared/components/Button';
import type { Invoice, CustomerSummary } from './types';

interface Props {
  invoice: Invoice;
  onClose: () => void;
}

function customerName(c: CustomerSummary): string {
  return [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
}

/**
 * Email a ONE_TIME invoice to a customer. Supports both registered customers (via a
 * merchant-scoped search picker) and off-system recipients (free-text email).
 */
export function SendToCustomerModal({ invoice, onClose }: Props) {
  const sendMutation = useSendInvoiceToCustomer(invoice.id);

  const [recipientEmail, setRecipientEmail] = useState(invoice.recipientEmail ?? '');
  const [recipientName, setRecipientName] = useState(invoice.recipientName ?? '');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [error, setError] = useState('');

  // Debounce the customer search so we don't query on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data: customers, isFetching } = useCustomerSearch(invoice.tenantId, debounced);

  const pickCustomer = (c: CustomerSummary) => {
    setRecipientEmail(c.email);
    setRecipientName(customerName(c));
    setSearch('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const email = recipientEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid email address');
      return;
    }
    await sendMutation.mutateAsync({ recipientEmail: email, recipientName: recipientName.trim() || undefined });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 overflow-y-auto py-8">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-[#28282A]">Send Invoice to Customer</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <p className="text-sm text-gray-500 mb-5">
          Email invoice <span className="font-mono text-gray-700">{invoice.invoiceNumber}</span> (PDF + payment link)
          to a customer. Pick a registered customer or type any email address.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Find a registered customer</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {debounced && (
              <div className="mt-1 max-h-44 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-50">
                {isFetching && <div className="px-3 py-2 text-xs text-gray-400">Searching…</div>}
                {!isFetching && (!customers || customers.length === 0) && (
                  <div className="px-3 py-2 text-xs text-gray-400">No matching customers — you can still type an email below.</div>
                )}
                {customers?.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => pickCustomer(c)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50"
                  >
                    <span className="block text-sm text-gray-800">{customerName(c) || c.email}</span>
                    <span className="block text-xs text-gray-500">{c.email}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Recipient email *</label>
            <input
              type="email"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              placeholder="customer@example.com"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Recipient name</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              placeholder="Optional — used in the email greeting"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
            />
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
              Cancel
            </button>
            <Button type="submit" disabled={sendMutation.isPending}>
              {sendMutation.isPending ? 'Sending…' : 'Send Invoice'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
